import React, { useCallback, useMemo, useState } from 'react';
import { IonButton, IonIcon, IonSpinner, IonText } from '@ionic/react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  type ColDef,
  type FilterChangedEvent,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  ModuleRegistry,
  type SelectionChangedEvent,
  themeAlpine,
} from 'ag-grid-community';
import {
  ActionGridActionColumn,
  ActionGridBulkAction,
  ActionGridIconAction,
  ActionGridIconActionContext,
  ActionGridProps,
  ActionGridRowModel,
} from '@/types/model/actionGrid';
import ActionIconButton from './ActionIconButton';

const ACTION_FIELD_PREFIX = '__actionGridAction__';

ModuleRegistry.registerModules([AllCommunityModule]);

interface GridRowRecord extends Record<string, unknown> {
  __actionGridRowId: string;
  __actionGridRowValues: unknown[];
}

const buildRowKey = (rowId: string, actionId: string): string => `${rowId}::${actionId}`;

const ActionGrid: React.FC<ActionGridProps> = ({
  columns,
  gridData,
  bulkActions = [],
  actionColumns = [],
  defaultColDef,
  onSelectionChanged,
  onGridReady,
  onFilterChanged,
  overlayNoRowsTemplate,
  pagination = true,
  paginationPageSize = 50,
  paginationPageSizeSelector = [25, 50, 100, 200],
  animateRows = true,
}) => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [selectedRows, setSelectedRows] = useState<ActionGridRowModel[]>([]);
  const [runningBulkActionId, setRunningBulkActionId] = useState<string | null>(null);
  const [runningRowActionKeys, setRunningRowActionKeys] = useState<Record<string, boolean>>({});

  const rowIdColumn = useMemo(() => columns.find((col) => col.isRowId), [columns]);

  const gridRows = useMemo<GridRowRecord[]>(() => {
    const idKey = rowIdColumn?.key;

    return gridData.map((values, rowIndex) => {
      const rowObject: GridRowRecord = {
        __actionGridRowId: '',
        __actionGridRowValues: values,
      };

      columns.forEach((column, colIndex) => {
        rowObject[column.key] = values[colIndex];
      });

      const rowIdValue = idKey ? rowObject[idKey] : undefined;
      const rowId = rowIdValue == null || rowIdValue === '' ? `row-${rowIndex}` : String(rowIdValue);
      rowObject.__actionGridRowId = rowId;

      return rowObject;
    });
  }, [columns, gridData, rowIdColumn]);

  const baseColumns = useMemo<ColDef<GridRowRecord>[]>(() => {
    return columns.map((column) => {
      const merged: ColDef<GridRowRecord> = {
        field: column.key,
        headerName: column.headerName,
        ...column.colDef,
      };

      return merged;
    });
  }, [columns]);

  const toActionGridRowModel = useCallback((record: GridRowRecord): ActionGridRowModel => {
    return {
      rowId: record.__actionGridRowId,
      rowValues: record.__actionGridRowValues,
      rowObject: Object.fromEntries(
        columns.map((column) => [column.key, record[column.key]])
      ),
    };
  }, [columns]);

  const runRowAction = useCallback(async (
    actionColumn: ActionGridActionColumn,
    action: ActionGridIconAction,
    rowModel: ActionGridRowModel
  ) => {
    const runningKey = buildRowKey(rowModel.rowId, action.id);

    setRunningRowActionKeys((prev) => ({ ...prev, [runningKey]: true }));

    try {
      const context: ActionGridIconActionContext = {
        row: rowModel,
        selectedRows,
        gridApi,
        actionColumnId: actionColumn.id,
        targetColumnKey: action.targetColumnKey,
      };
      await action.onAction(context);
    } finally {
      setRunningRowActionKeys((prev) => {
        const next = { ...prev };
        delete next[runningKey];
        return next;
      });
    }
  }, [gridApi, selectedRows]);

  const actionColDefs = useMemo<ColDef<GridRowRecord>[]>(() => {
    return actionColumns.map((actionColumn) => ({
      field: `${ACTION_FIELD_PREFIX}${actionColumn.id}`,
      headerName: actionColumn.headerName ?? '',
      width: actionColumn.width ?? 120,
      minWidth: actionColumn.width ?? 120,
      maxWidth: actionColumn.width ? actionColumn.width + 40 : 180,
      pinned: actionColumn.pinned ?? 'right',
      sortable: false,
      filter: false,
      resizable: true,
      suppressMovable: true,
      suppressCsvExport: true,
      cellRenderer: (params: ICellRendererParams<GridRowRecord>) => {
        const data = params.data;
        if (!data) {
          return null;
        }

        const rowModel = toActionGridRowModel(data);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '100%' }}>
            {actionColumn.actions.map((action) => {
              const actionContext: ActionGridIconActionContext = {
                row: rowModel,
                selectedRows,
                gridApi,
                actionColumnId: actionColumn.id,
                targetColumnKey: action.targetColumnKey,
              };

              if (action.isVisible && !action.isVisible(actionContext)) {
                return null;
              }

              const runningKey = buildRowKey(rowModel.rowId, action.id);
              const isRunning = Boolean(runningRowActionKeys[runningKey]);
              const isDisabled = isRunning || Boolean(action.isDisabled?.(actionContext));

              return (
                <IonButton
                  key={`${actionColumn.id}-${action.id}`}
                  fill="clear"
                  size="small"
                  title={action.title}
                  onClick={() => {
                    void runRowAction(actionColumn, action, rowModel);
                  }}
                  disabled={isDisabled}
                  style={{ '--padding-start': '4px', '--padding-end': '4px' }}
                >
                  {isRunning
                    ? <IonSpinner name="crescent" style={{ width: '16px', height: '16px' }} />
                    : <IonIcon icon={action.icon} slot="icon-only" style={{ fontSize: '18px' }} />}
                </IonButton>
              );
            })}
          </div>
        );
      },
    }));
  }, [actionColumns, gridApi, runRowAction, runningRowActionKeys, selectedRows, toActionGridRowModel]);

  const allColDefs = useMemo<ColDef<GridRowRecord>[]>(() => {
    return [...baseColumns, ...actionColDefs];
  }, [actionColDefs, baseColumns]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
    onGridReady?.(event);
  }, [onGridReady]);

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const rows = event.api.getSelectedRows() as GridRowRecord[];
    const mapped = rows.map((row) => toActionGridRowModel(row));

    setSelectedRows(mapped);
    onSelectionChanged?.(mapped);
  }, [onSelectionChanged, toActionGridRowModel]);

  const handleFilterChanged = useCallback((event: FilterChangedEvent) => {
    onFilterChanged?.(event);
  }, [onFilterChanged]);

  const runBulkAction = useCallback(async (action: ActionGridBulkAction) => {
    const context = { selectedRows, gridApi };

    setRunningBulkActionId(action.id);
    try {
      await action.onAction(context);
    } finally {
      setRunningBulkActionId(null);
    }
  }, [gridApi, selectedRows]);

  const bulkActionContext = useMemo(() => ({ selectedRows, gridApi }), [selectedRows, gridApi]);

  const sortedBulkActions = useMemo(() => {
    return bulkActions
      .filter((action) => !action.isVisible || action.isVisible(bulkActionContext))
      .map((action, index) => ({ action, index }))
      .sort((a, b) => {
        const groupDiff = (a.action.group ?? 0) - (b.action.group ?? 0);
        if (groupDiff !== 0) return groupDiff;
        const orderDiff = (a.action.order ?? 0) - (b.action.order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return a.index - b.index;
      })
      .map((item) => item.action);
  }, [bulkActions, bulkActionContext]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', height: '100%' }}>
      {bulkActions.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            borderRadius: '8px',
          }}
        >
          {selectedRows.length > 0 && (
            <IonText style={{ fontSize: '0.85rem', color: '#444' }}>
              <strong>{selectedRows.length}</strong> selected
            </IonText>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginLeft: 'auto' }}>
            {sortedBulkActions.map((action, index) => {
              const isRunning = runningBulkActionId === action.id;
              const isDisabled = isRunning || Boolean(action.isDisabled?.(bulkActionContext));
              const currentGroup = action.group ?? 0;
              const previousGroup = index > 0 ? (sortedBulkActions[index - 1].group ?? 0) : null;
              const showSeparator = previousGroup !== null && previousGroup !== currentGroup;

              return (
                <React.Fragment key={action.id}>
                  {showSeparator && (
                    <div style={{ width: '1px', height: '24px', alignSelf: 'center', background: 'var(--ion-color-light-shade)' }} />
                  )}
                  <ActionIconButton
                    icon={action.icon}
                    label={action.label}
                    disabled={isDisabled}
                    loading={isRunning}
                    onClick={() => {
                      void runBulkAction(action);
                    }}
                    buttonStyle={{ margin: '0' }}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div
        style={{
          flex: 1,
          minHeight: '360px',
          width: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--ion-color-light-shade)',
        }}
      >
        <AgGridReact<GridRowRecord>
          theme={themeAlpine}
          rowData={gridRows}
          columnDefs={allColDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.__actionGridRowId}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: true,
            headerCheckbox: true,
            enableClickSelection: true,
            selectAll: 'filtered',
          }}
          onGridReady={handleGridReady}
          onSelectionChanged={handleSelectionChanged}
          onFilterChanged={handleFilterChanged}
          animateRows={animateRows}
          pagination={pagination}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          overlayNoRowsTemplate={overlayNoRowsTemplate}
        />
      </div>
    </div>
  );
};

export default ActionGrid;
